/**
 * These are the APIs that you should use to interact with the resource cache
 */
import { DEFAULT_MAX_AGE } from './constants';
import {
    resourceLoadStart,
    resourceUpdate,
    resourceInvalidate,
} from './actions';

const makeAgeTest = (seconds) => ({ timestamp }) =>
    Date.now() - timestamp <= seconds * 1000;

/**
 * @param {import('redux').Dispatch} dispatch - the store's dispatch function
 * @param {string} cacheId - the identifier for the cache to prepare this resource in (where you've mounted the reducer)
 * @param {string} resourceId - the identifier for this resource
 * @param {() => Promise<boolean>} getResourcePromise - a function that will be called with no arguments if this resource should be loaded
 *                             this should return a promise
 * @param {() => boolean} testValidity - a function used to determine if we need to update the resource. called with `resource`
 * @returns {Promise}
 */
export const prepareResource = (
    dispatch,
    cacheId,
    resourceId,
    getResourcePromise,
    testValidity = makeAgeTest(DEFAULT_MAX_AGE),
) =>
    new Promise((resolve, reject) => {
        dispatch(
            resourceLoadStart(
                cacheId,
                resourceId,
                getResourcePromise,
                testValidity,
                resolve,
                reject,
            ),
        );
    });

/**
 * Directly updates the cache value for a specific resource
 *
 * @param {import('redux').Dispatch} dispatch
 * @param {string} cacheId
 * @param {string} resourceId
 * @param {object} payload
 */
export const updateResource = (dispatch, cacheId, resourceId, payload) =>
    dispatch(resourceUpdate(cacheId, resourceId, payload));

/**
 * Marks the designated resource as Invalid
 *
 * @param {import('redux').Dispatch} dispatch
 * @param {string} cacheId
 * @param {string} resourceId
 */
export const invalidateResource = (dispatch, cacheId, resourceId) =>
    dispatch(resourceInvalidate(cacheId, resourceId));

/**
 * Test if a resource is ready to be accessed
 *
 * @param {object} cache
 * @param {string} resourceId
 * @returns boolean
 */
export const resourceIsReady = (cache, resourceId) =>
    (cache &&
        cache[resourceId] &&
        !cache[resourceId].inProgress &&
        !cache[resourceId].error) ||
    false;

/**
 * Get the error associated with a resource
 *
 * @param {object} cache
 * @param {string} resourceId
 */
export const getResourceError = (cache, resourceId) =>
    (cache && cache[resourceId] && cache[resourceId].error) || undefined;

/**
 * Get the error detail associated with a resource
 *
 * @param {object} cache
 * @param {string} resourceId
 */
export const getResourceErrorDetail = (cache, resourceId) =>
    (cache && cache[resourceId] && cache[resourceId].errorDetail) || undefined;

/**
 * Gets the resource's payload if it is ready, otherwise returns the defaultValue (undefined by default)
 *
 * @param {object} cache
 * @param {string} resourceId
 * @param {object} defaultValue (optional)
 */
export const getResource = (cache, resourceId, defaultValue) =>
    resourceIsReady(cache, resourceId)
        ? cache[resourceId].payload
        : defaultValue;

/**
 * Gets the resource's payload if it's available regardless of inProgress or error flags, otherwise
 * returns the defaultValue (undefined by default)
 *
 * @param {object} cache
 * @param {string} resourceId
 * @param {object} defaultValue (optional)
 */
export const getCachedResource = (cache, resourceId, defaultValue) =>
    cache && cache[resourceId] && cache[resourceId].payload
        ? cache[resourceId].payload
        : defaultValue;
